/**
 * AnalysisProgress
 *
 * Full-screen overlay showing document analysis progress with:
 * - Animated progress bar
 * - Stage-by-stage status updates
 * - Clear completion state with action buttons
 * - WebSocket real-time updates (with polling fallback)
 * - Live streaming preview of Phase 3 analysis (when enabled)
 *
 * Designed for non-technical users who need clear feedback.
 */

import React, { useMemo, useEffect, useState, useRef, useCallback, useContext } from 'react';
import {
    View,
    Text,
    Modal,
    ActivityIndicator,
    StyleSheet,
    TouchableOpacity,
    ScrollView,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useTranslation } from '../../hooks/useTranslation';
import { useTaskEvents, useTaskProgress, useWebSocketConnection } from '../../hooks/useTaskProgress';
import MarkdownRenderer from '../MarkdownRenderer';
import { taskAPI } from '../../services/api';
import { jobStaleNotice, localizedJobError, type JobErrorContext, type JobStaleNotice } from './jobProgress';
import TrialContext from '../../context/TrialContext';
import { brand } from '../../design-system/theme/tokens';

// Design tokens
const COLORS = {
    primary: brand.primary,
    success: '#10B981',
    error: '#EF4444',
    background: '#FFFFFF',
    surface: '#F9FAFB',
    text: {
        primary: '#111827',
        secondary: '#4B5563',
        muted: '#9CA3AF',
    },
    border: '#E5E7EB',
};

interface AnalysisProgressProps {
    visible: boolean;
    onClose: () => void;
    /** Celery task ID for WebSocket progress updates */
    taskId?: string | null;
    /** Analysis session ID (available after completion) */
    sessionId?: number | null;
    /** Callbacks for action buttons */
    onViewAnalysis?: (sessionId: number) => void;
    onExportPdf?: (sessionId: number) => void;
    /** Called when analysis completes successfully */
    onComplete?: (sessionId: number) => void;
    /** Called when analysis fails */
    onError?: (error: string) => void;
    /** Called after the user cancels the running task */
    onCancel?: (taskId?: string | null) => void;
    /** Optional "Try again" action shown after a terminal failure. */
    onRetry?: () => void;
    /** Document/transcript name being analyzed */
    documentName?: string;
}

// Canonical analysis phases. Keep these aligned with backend stages in
// backend/app/tasks/ai_tasks.py and backend/app/ai_runtime/workflows/grading/nodes.py.
const ANALYSIS_STAGES = [
    {
        id: 'preparing',
        key: 'analysis.progress.preparing',
        fallback: 'Preparing...',
        detailFallback: 'Preparing the analysis type, language, and session settings.',
        stageIds: ['queued', 'pending', 'starting', 'preparing', 'processing', 'planning', 'policy_load', 'normalizing'],
        minProgress: 5,
        remainingMs: 11 * 60_000,
    },
    {
        id: 'reading',
        key: 'analysis.progress.reading',
        fallback: 'Reading transcript...',
        detailFallback: 'Checking the coach/client flow, timestamps, and session structure in the transcript.',
        stageIds: ['reading', 'loading_document', 'extracting'],
        minProgress: 25,
        remainingMs: 9 * 60_000,
    },
    {
        id: 'context',
        key: 'analysis.progress.context',
        fallback: 'Gathering context...',
        detailFallback: 'Preparing ICF competency indicators and relevant level context.',
        stageIds: ['resolving_context', 'searching'],
        minProgress: 35,
        remainingMs: 8 * 60_000,
    },
    {
        id: 'scoring',
        key: 'analysis.progress.scoring',
        fallback: 'Evaluating competency evidence...',
        detailFallback: 'Matching behavioral evidence to each competency.',
        stageIds: ['analyzing', 'screening', 'scoring', 'analysis_legacy', 'custom_analysis'],
        minProgress: 50,
        remainingMs: 6 * 60_000,
    },
    {
        id: 'writing',
        key: 'analysis.progress.writing',
        fallback: 'Structuring developmental feedback...',
        detailFallback: 'Structuring feedback with strengths, development focus, and evidence quotes.',
        stageIds: ['generating', 'generating_response', 'analysis_pass', 'refining', 'rendering'],
        minProgress: 65,
        remainingMs: 4 * 60_000,
    },
    {
        id: 'validating',
        key: 'analysis.progress.validating',
        fallback: 'Checking level and evidence consistency...',
        detailFallback: 'Rechecking the level decision, evidence, and feedback consistency.',
        stageIds: ['validating', 'icf_extraction', 'persisting'],
        minProgress: 80,
        remainingMs: 2 * 60_000,
    },
    {
        id: 'saving',
        key: 'analysis.progress.saving',
        fallback: 'Saving results...',
        detailFallback: 'Saving the report to chat and your session history.',
        stageIds: ['saving', 'complete'],
        minProgress: 90,
        remainingMs: 60_000,
    },
];

const STAGE_MAP = ANALYSIS_STAGES.reduce<Record<string, number>>((acc, phase, index) => {
    phase.stageIds.forEach((stageId) => {
        acc[stageId] = index;
    });
    return acc;
}, {});

const TASK_CREATION_TIMEOUT_MS = 120_000;
// Stale thresholds live in ./jobProgress: they only change the notice shown,
// never fail the job client-side (the server owns terminal state).
const ETA_TICK_MS = 1000;
const DEFAULT_TOTAL_ANALYSIS_MS = 12 * 60_000;
const MIN_ETA_PROGRESS = 5;

const STAGE_REMAINING_MS: Record<string, number> = {
    queued: 12 * 60_000,
    pending: 12 * 60_000,
    starting: 12 * 60_000,
    preparing: 11 * 60_000,
    processing: 11 * 60_000,
    planning: 11 * 60_000,
    policy_load: 11 * 60_000,
    normalizing: 10 * 60_000,
    reading: 10 * 60_000,
    resolving_context: 10 * 60_000,
    extracting: 9 * 60_000,
    searching: 8 * 60_000,
    analyzing: 8 * 60_000,
    screening: 7 * 60_000,
    scoring: 6 * 60_000,
    analysis_legacy: 6 * 60_000,
    custom_analysis: 6 * 60_000,
    generating: 5 * 60_000,
    generating_response: 5 * 60_000,
    analysis_pass: 4 * 60_000,
    refining: 3 * 60_000,
    validating: 2 * 60_000,
    icf_extraction: 2 * 60_000,
    persisting: 90_000,
    rendering: 90_000,
    saving: 60_000,
    complete: 0,
    error: 0,
};

type EffectiveAnalysisState = {
    status: 'processing' | 'completed' | 'failed';
    progress: number;
    stage: string;
    error: string | null;
};

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function hasRealProgressSignal(progress: { stage?: string; message?: string; progress?: number } | null): boolean {
    if (!progress) return false;
    return Boolean(progress.stage || progress.message || (progress.progress ?? 0) > 0);
}

function normalizeStageId(stage: string | undefined): string {
    return (stage || '').trim().toLowerCase();
}

function progressBucketIndex(progress: number | undefined): number {
    const value = Number.isFinite(progress) ? Number(progress) : 0;
    let index = 0;
    ANALYSIS_STAGES.forEach((phase, phaseIndex) => {
        if (value >= phase.minProgress) {
            index = phaseIndex;
        }
    });
    return index;
}

function isErrorStage(stage: string | undefined): boolean {
    const value = normalizeStageId(stage);
    return value === 'error' || value === 'failed' || value === 'failure';
}

function phaseIndexForStage(stage: string | undefined, progress?: number): number {
    const value = Number.isFinite(progress) ? Number(progress) : 0;
    const normalizedStage = normalizeStageId(stage);
    const progressIndex = progressBucketIndex(value);

    // Some backend stage ids are intentionally reused at different points in
    // the pipeline. Use progress to keep checkpoints aligned with what the user
    // is actually waiting on instead of moving backward.
    if (normalizedStage === 'extracting' && value >= 50) {
        return STAGE_MAP.screening;
    }
    if (normalizedStage === 'analyzing' && value < 50) {
        return STAGE_MAP.resolving_context;
    }

    const stageIndex = STAGE_MAP[normalizedStage];
    if (stageIndex === undefined) {
        return progressIndex;
    }

    // If a known stage arrives with a later percentage, prefer the percentage
    // bucket. This protects the UI from backend stage aliases and older workers.
    return clamp(Math.max(stageIndex, progressIndex), 0, ANALYSIS_STAGES.length - 1);
}

function phaseForStage(stage: string | undefined, progress?: number) {
    return ANALYSIS_STAGES[phaseIndexForStage(stage, progress)];
}

function remainingMsForStage(stage: string | undefined, progress: number): number {
    const normalizedStage = normalizeStageId(stage);
    const phase = phaseForStage(stage, progress);
    if (normalizedStage === 'extracting' && progress >= 50) {
        return phase.remainingMs;
    }
    if (normalizedStage === 'analyzing' && progress < 50) {
        return phase.remainingMs;
    }
    return STAGE_REMAINING_MS[normalizedStage] ?? phase.remainingMs;
}

function normalizeAnalysisProgress(stage: string | undefined, rawProgress: number | undefined): number {
    if (normalizeStageId(stage) === 'complete') return 100;
    const value = Number.isFinite(rawProgress) ? Number(rawProgress) : 0;
    const phase = phaseForStage(stage, value);
    return clamp(Math.max(value, phase.minProgress), phase.minProgress, 99);
}

function chooseAnalysisState(
    websocketState: EffectiveAnalysisState | null,
    polledState: EffectiveAnalysisState | null
): EffectiveAnalysisState | null {
    if (!websocketState) return polledState;
    if (!polledState) return websocketState;
    if (websocketState.status !== 'processing') return websocketState;
    if (polledState.status !== 'processing') return polledState;

    const wsIndex = phaseIndexForStage(websocketState.stage, websocketState.progress);
    const pollIndex = phaseIndexForStage(polledState.stage, polledState.progress);
    if (pollIndex > wsIndex) return polledState;
    if (wsIndex > pollIndex) return websocketState;

    return (polledState.progress || 0) > (websocketState.progress || 0)
        ? polledState
        : websocketState;
}

type PolledAnalysisState = {
    taskId: string;
    state: EffectiveAnalysisState;
};

type LastDisplayedAnalysisState = {
    taskId: string | null;
    state: EffectiveAnalysisState;
};

export function AnalysisProgress({
    visible,
    onClose,
    taskId,
    sessionId,
    onViewAnalysis,
    onExportPdf,
    onComplete,
    onError,
    onCancel,
    onRetry,
    documentName,
}: AnalysisProgressProps) {
    const { t, language } = useTranslation();
    // Worker-side trial block carries no date; the trial expiry is the plan start.
    // Optional context: this modal can render outside a TrialProvider.
    const trialContext = useContext(TrialContext);
    const jobErrorContextRef = useRef<JobErrorContext>({});
    jobErrorContextRef.current = { language, planStartsAt: trialContext?.trialStatus?.expires_at ?? null };
    const { useFallback: wsUnavailable } = useWebSocketConnection();
    const { onStreamDelta, onStreamError, onStreamReset } = useTaskEvents();
    const [completedSessionId, setCompletedSessionId] = useState<number | null>(null);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [taskCreationTimedOut, setTaskCreationTimedOut] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [polledState, setPolledState] = useState<PolledAnalysisState | null>(null);
    const [taskStartedAt, setTaskStartedAt] = useState<number | null>(null);
    const [nowMs, setNowMs] = useState(() => Date.now());
    const scrollViewRef = useRef<ScrollView>(null);
    const activeTaskIdRef = useRef<string | null>(null);
    const activeStageRef = useRef<string | null>(null);
    const stageStartedAtRef = useRef<number | null>(null);
    const lastDisplayedStateRef = useRef<LastDisplayedAnalysisState | null>(null);
    const terminalTaskIdRef = useRef<string | null>(null);
    // Presentation-only stale notice ("still waiting" / "lost track").
    const [staleNotice, setStaleNotice] = useState<JobStaleNotice>(null);
    const [isCheckingAgain, setIsCheckingAgain] = useState(false);
    const waitingDismissedRef = useRef(false);
    const lostTrackResetAtRef = useRef<number | null>(null);
    const pollNowRef = useRef<(() => void) | null>(null);

    const failTaskOnce = useCallback((message: string) => {
        if (terminalTaskIdRef.current === taskId) return;
        terminalTaskIdRef.current = taskId || null;
        setHasError(true);
        setErrorMessage(message);
        onError?.(message);
    }, [onError, taskId]);

    const completeTaskOnce = useCallback((result?: Record<string, any>) => {
        if (terminalTaskIdRef.current === taskId) return;
        const resultStatus = String(result?.status || '').toUpperCase();
        if (resultStatus === 'FAILED' || resultStatus === 'FAILURE') {
            failTaskOnce(localizedJobError(t, result as any, t('analysis.progress.failed'), jobErrorContextRef.current));
            return;
        }
        const newSessionId = result?.session_id || sessionId;
        if (!newSessionId) {
            failTaskOnce(t('analysis.progress.missingResult'));
            return;
        }
        terminalTaskIdRef.current = taskId || null;
        setCompletedSessionId(newSessionId);
        onComplete?.(newSessionId);
    }, [failTaskOnce, onComplete, sessionId, t, taskId]);

    useEffect(() => {
        terminalTaskIdRef.current = null;
        waitingDismissedRef.current = false;
        lostTrackResetAtRef.current = null;
        setStaleNotice(null);
    }, [taskId]);

    // Force re-render counter for streaming callbacks
    const [, forceUpdate] = useState(0);

    // Use WebSocket progress when available
    const wsProgress = useTaskProgress(taskId, {
        onComplete: (result) => {
            console.log('[AnalysisProgress] Task completed via WebSocket:', result);
            completeTaskOnce(result);
        },
        onError: (error, details) => {
            console.warn('[AnalysisProgress] Task error via WebSocket:', error);
            // Translated from the stable error code; English text only when no code.
            failTaskOnce(localizedJobError(
                t,
                { error_code: details?.errorCode, error_message_key: details?.errorMessageKey, error },
                t('analysis.progress.failed'),
                jobErrorContextRef.current,
            ));
        },
    });

    // Register streaming callbacks for re-render triggers
    useEffect(() => {
        if (!taskId) return;
        const unregDelta = onStreamDelta(taskId, () => forceUpdate(n => n + 1));
        const unregError = onStreamError(taskId, () => forceUpdate(n => n + 1));
        const unregReset = onStreamReset(taskId, () => forceUpdate(n => n + 1));
        return () => { unregDelta(); unregError(); unregReset(); };
    }, [taskId, onStreamDelta, onStreamError, onStreamReset]);

    // Source of truth: wsProgress.streamingText (preserved by task_progress/task_complete handlers)
    const streamingText = wsProgress?.streamingText || '';
    const isStreaming = wsProgress?.isStreaming || false;

    // Derive state from WebSocket progress
    const effectiveState: EffectiveAnalysisState = useMemo(() => {
        if (hasError) {
            return { status: 'failed', progress: 0, stage: '', error: errorMessage };
        }

        if (visible && !taskId) {
            if (taskCreationTimedOut) {
                return {
                    status: 'failed',
                    progress: 0,
                    stage: '',
                    error: t('analysis.progress.startFailed'),
                };
            }

            return { status: 'processing', progress: 5, stage: 'starting', error: null };
        }

        if (completedSessionId) {
            return { status: 'completed', progress: 100, stage: 'complete', error: null };
        }

        let websocketState: EffectiveAnalysisState | null = null;
        if (wsProgress && !wsUnavailable && hasRealProgressSignal(wsProgress)) {
            const stage = wsProgress.stage || 'starting';
            const rawWsError = wsProgress.error || (isErrorStage(stage) ? (wsProgress.message || 'Analysis failed') : null);
            const wsError = rawWsError
                ? localizedJobError(
                    t,
                    { error_code: wsProgress.errorCode, error_message_key: wsProgress.errorMessageKey, error: rawWsError },
                    rawWsError,
                    jobErrorContextRef.current,
                )
                : null;
            websocketState = {
                status: wsProgress.isComplete
                    ? (wsError ? 'failed' : 'completed')
                    : wsError
                        ? 'failed'
                        : 'processing',
                progress: normalizeAnalysisProgress(stage, wsProgress.progress),
                stage,
                error: wsError,
            };
        }

        const currentPolledState = polledState?.taskId === taskId ? polledState.state : null;
        const selectedState = chooseAnalysisState(websocketState, currentPolledState) || {
            status: 'processing',
            progress: 5,
            stage: 'starting',
            error: null,
        };

        const taskKey = taskId || null;
        const lastDisplayed = lastDisplayedStateRef.current?.taskId === taskKey
            ? lastDisplayedStateRef.current.state
            : null;

        if (selectedState.status === 'processing') {
            const stableState = chooseAnalysisState(selectedState, lastDisplayed) || selectedState;
            lastDisplayedStateRef.current = { taskId: taskKey, state: stableState };
            return stableState;
        }

        lastDisplayedStateRef.current = null;
        return selectedState;
    }, [visible, taskId, taskCreationTimedOut, wsProgress, wsUnavailable, polledState, completedSessionId, hasError, errorMessage, t]);

    const isCompleted = effectiveState.status === 'completed';
    const isFailed = effectiveState.status === 'failed';
    const currentStageIndex = phaseIndexForStage(effectiveState.stage, effectiveState.progress);

    // Show preview: whenever we have streaming text AND task is still running
    const showStreamPreview = streamingText.length > 0 && !isCompleted && !isFailed;
    const isPossiblyStalled = Boolean(wsProgress?.mayBeStalled && !wsUnavailable && !isCompleted && !isFailed);

    const handleCancel = async () => {
        if (isCancelling || isCompleted || isFailed) return;
        setCancelError(null);
        setIsCancelling(true);
        try {
            if (taskId) {
                await taskAPI.cancel(taskId);
            }
            onCancel?.(taskId);
            if (!onCancel) {
                onClose();
            }
        } catch (error) {
            console.warn('[AnalysisProgress] Cancel failed:', error);
            setCancelError(t('analysis.progress.cancelFailed'));
            setIsCancelling(false);
        }
    };

    const etaText = useMemo(() => {
        if (!visible || isCompleted || isFailed || !taskStartedAt) return null;

        const progress = clamp(effectiveState.progress || 0, 0, 99);
        const elapsedMs = Math.max(0, nowMs - taskStartedAt);
        const formatDuration = (durationMs: number): string => {
            const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
            if (totalSeconds < 60) {
                return t('analysis.progress.secondsShort', { seconds: totalSeconds });
            }
            const minutes = Math.max(1, Math.round(totalSeconds / 60));
            return t('analysis.progress.minutesShort', { minutes });
        };
        const elapsedText = formatDuration(elapsedMs);

        if (!taskId || progress < MIN_ETA_PROGRESS) {
            return t(
                'analysis.progress.elapsedOnly',
                { elapsed: elapsedText }
            );
        }

        if (elapsedMs > DEFAULT_TOTAL_ANALYSIS_MS && progress <= 10) {
            return t(
                'analysis.progress.elapsedOnly',
                { elapsed: elapsedText }
            );
        }

        const progressBasedTotalMs = (elapsedMs / Math.max(progress, MIN_ETA_PROGRESS)) * 100;
        const progressBasedRemainingMs = Math.max(30_000, progressBasedTotalMs - elapsedMs);
        const stageStartedAt = stageStartedAtRef.current;
        const stageElapsedMs = stageStartedAt ? Math.max(0, nowMs - stageStartedAt) : elapsedMs;
        const stageRemainingMs = remainingMsForStage(effectiveState.stage, progress)
            ?? DEFAULT_TOTAL_ANALYSIS_MS * ((100 - progress) / 100);
        const stagePriorRemainingMs = Math.max(30_000, stageRemainingMs - stageElapsedMs);

        // Blend observed progress with stage priors. For long LLM calls, elapsed
        // time is the best signal, but stage priors stop the estimate from being
        // wildly optimistic early in the run.
        const progressWeight = progress >= 35 ? 0.55 : progress >= 20 ? 0.35 : progress >= 10 ? 0.15 : 0;
        const blendedRemainingMs = Math.max(
            30_000,
            (progressBasedRemainingMs * progressWeight) + (stagePriorRemainingMs * (1 - progressWeight))
        );
        const cappedRemainingMs = Math.min(
            blendedRemainingMs,
            Math.max(stagePriorRemainingMs + 2 * 60_000, stagePriorRemainingMs * 1.5)
        );
        const lowerMs = Math.max(30_000, cappedRemainingMs * 0.75);
        const upperMs = Math.max(lowerMs, cappedRemainingMs * 1.35);

        const remainingText = upperMs < 60_000
            ? t('analysis.progress.etaUnderMinute')
            : Math.abs(upperMs - lowerMs) < 90_000
                ? t('analysis.progress.etaAboutDuration', { duration: formatDuration(upperMs) })
                : t('analysis.progress.etaRangeDuration', {
                    min: formatDuration(lowerMs),
                    max: formatDuration(upperMs),
                });

        return t(
            'analysis.progress.etaWithElapsed',
            { elapsed: elapsedText, remaining: remainingText }
        );
    }, [visible, isCompleted, isFailed, taskStartedAt, nowMs, effectiveState.progress, effectiveState.stage, taskId, t]);

    const checkpointText = useMemo(() => {
        if (isCompleted || isFailed) return null;
        const checkpointIndex = clamp(currentStageIndex, 0, ANALYSIS_STAGES.length - 1);
        const stage = ANALYSIS_STAGES[checkpointIndex];
        const label = t(stage.key, stage.fallback);
        return t(
            'analysis.progress.checkpoint',
            {
                current: checkpointIndex + 1,
                total: ANALYSIS_STAGES.length,
                label,
            }
        );
    }, [currentStageIndex, isCompleted, isFailed, t]);

    // Get stages with completion status
    const stages = useMemo(() => {
        return ANALYSIS_STAGES.map((stage, index) => ({
            ...stage,
            label: t(stage.key, stage.fallback),
            detail: t(`analysis.progress.stageDetail.${stage.id}`, stage.detailFallback),
            completed: isCompleted || index < currentStageIndex,
            active: !isCompleted && !isFailed && index === currentStageIndex,
        }));
    }, [t, currentStageIndex, isCompleted, isFailed]);

    // Get current stage message
    const stageMessage = useMemo(() => {
        if (isCompleted) return t('analysis.progress.complete') || 'Analysis complete!';
        if (isFailed) return effectiveState.error || t('analysis.progress.failed') || 'Analysis failed';
        if (visible && !taskId) return t('analysis.progress.starting');
        const currentStage = stages.find(s => s.active);
        return currentStage?.label || t('analysis.progress.analyzing') || 'Analyzing...';
    }, [isCompleted, isFailed, effectiveState.error, visible, taskId, stages, t]);

    const stageDetail = useMemo(() => {
        if (isCompleted || isFailed) return null;
        const currentStage = stages.find(s => s.active);
        return currentStage?.detail || t('analysis.progress.detailReason');
    }, [isCompleted, isFailed, stages, t]);

    // Reset state when modal becomes visible with new task
    useEffect(() => {
        if (visible) {
            setCompletedSessionId(null);
            setHasError(false);
            setErrorMessage(null);
            setTaskCreationTimedOut(false);
            setIsCancelling(false);
            setCancelError(null);
        }
    }, [visible, taskId]);

    useEffect(() => {
        if (!visible) {
            activeTaskIdRef.current = null;
            activeStageRef.current = null;
            stageStartedAtRef.current = null;
            lastDisplayedStateRef.current = null;
            setTaskStartedAt(null);
            setPolledState(null);
            return;
        }

        if (!taskStartedAt) {
            const startedAt = Date.now();
            setTaskStartedAt(startedAt);
            stageStartedAtRef.current = startedAt;
        }

        if (taskId && activeTaskIdRef.current !== taskId) {
            const startedAt = Date.now();
            activeTaskIdRef.current = taskId;
            activeStageRef.current = effectiveState.stage || 'starting';
            lastDisplayedStateRef.current = null;
            setTaskStartedAt(startedAt);
            stageStartedAtRef.current = startedAt;
            setPolledState(null);
        }
    }, [visible, taskId, taskStartedAt, effectiveState.stage]);

    useEffect(() => {
        if (!visible || isCompleted || isFailed) return;

        const nextStage = effectiveState.stage || 'starting';
        if (activeStageRef.current !== nextStage) {
            activeStageRef.current = nextStage;
            stageStartedAtRef.current = Date.now();
        }
    }, [visible, effectiveState.stage, isCompleted, isFailed]);

    useEffect(() => {
        if (!visible || isCompleted || isFailed) return;

        const intervalId = setInterval(() => setNowMs(Date.now()), ETA_TICK_MS);
        return () => clearInterval(intervalId);
    }, [visible, isCompleted, isFailed]);

    // If the parent opens the modal before the API returns a task ID, show a
    // real starting state and fail visibly if task creation never completes.
    useEffect(() => {
        if (!visible || taskId || completedSessionId || hasError) return;

        const timeoutId = setTimeout(() => {
            const errMsg = t('analysis.progress.startFailed');
            setTaskCreationTimedOut(true);
            setHasError(true);
            setErrorMessage(errMsg);
            onError?.(errMsg);
        }, TASK_CREATION_TIMEOUT_MS);

        return () => clearTimeout(timeoutId);
    }, [visible, taskId, completedSessionId, hasError, onError, t]);

    // Polling safety net — covers WebSocket fallback mode AND any case where the
    // server's task_complete event is missed (e.g. WS reconnect during the brief
    // completion window). Runs while the modal is visible and not yet completed.
    useEffect(() => {
        if (!visible || !taskId) return;
        if (completedSessionId || hasError) return;

        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const startedAt = Date.now();
        let lastStatusAt = startedAt;
        let bestProgress = 0;

        // ASYNC-6: never give up client-side. A job that looks stale may just
        // be queued behind other analyses; failing here (and letting the user
        // re-submit) is what produced duplicate charged analyses. The server
        // (job row + reaper) sends the terminal event; we only change the
        // notice and keep polling.
        const updateStaleNotice = () => {
            setStaleNotice(jobStaleNotice({
                now: Date.now(),
                startedAt,
                lastStatusAt,
                bestProgress,
                waitingDismissed: waitingDismissedRef.current,
                lostTrackResetAt: lostTrackResetAtRef.current,
            }));
        };

        const failedMessage = t('analysis.progress.failed');

        const poll = async () => {
            try {
                const status = await taskAPI.getStatus(taskId);
                if (cancelled) return;
                lastStatusAt = Date.now();

                if (status.status === 'SUCCESS') {
                    completeTaskOnce((status as any).result || {});
                    return;
                }

                if (status.status === 'FAILURE' || status.status === 'REVOKED') {
                    failTaskOnce(localizedJobError(t, status as any, failedMessage, jobErrorContextRef.current));
                    return;
                }

                const fallbackStage = activeStageRef.current || 'starting';
                const nextStage = status.stage || fallbackStage;
                if (isErrorStage(nextStage)) {
                    failTaskOnce(localizedJobError(t, status as any, failedMessage, jobErrorContextRef.current));
                    return;
                }

                const nextProgress = normalizeAnalysisProgress(nextStage, status.progress);
                bestProgress = Math.max(bestProgress, nextProgress);
                setPolledState({
                    taskId,
                    state: {
                        status: 'processing',
                        progress: nextProgress,
                        stage: nextStage,
                        error: null,
                    },
                });

                // Still running — schedule next poll
                updateStaleNotice();
                timeoutId = setTimeout(poll, 5000);
            } catch (err) {
                // Network blip — try again later, don't fail the modal
                if (!cancelled) {
                    updateStaleNotice();
                    timeoutId = setTimeout(poll, 8000);
                }
            }
        };

        // "Check again": poll immediately and restart the lost-track clock.
        pollNowRef.current = () => {
            if (cancelled) return;
            if (timeoutId) clearTimeout(timeoutId);
            void poll();
        };

        // First poll after 6s — give WebSocket a head start; this is purely a safety net.
        timeoutId = setTimeout(poll, 6000);

        return () => {
            cancelled = true;
            pollNowRef.current = null;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [visible, taskId, completedSessionId, hasError, completeTaskOnce, failTaskOnce, t]);

    const handleKeepWaiting = () => {
        waitingDismissedRef.current = true;
        setStaleNotice(null);
    };

    const handleCheckAgain = () => {
        lostTrackResetAtRef.current = Date.now();
        setIsCheckingAgain(true);
        setStaleNotice(null);
        pollNowRef.current?.();
        setTimeout(() => setIsCheckingAgain(false), 1500);
    };

    const finalSessionId = completedSessionId || sessionId;

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            statusBarTranslucent={true}
            onRequestClose={!isCompleted && !isFailed ? handleCancel : onClose}
        >
            <View style={styles.overlay}>
                <View style={styles.container}>
                    {/* Header */}
                    <View style={styles.header}>
                        <MaterialIcons
                            name={isCompleted ? 'check-circle' : isFailed ? 'error' : 'analytics'}
                            size={56}
                            color={isCompleted ? COLORS.success : isFailed ? COLORS.error : COLORS.primary}
                        />
                        <Text style={styles.title}>
                            {isCompleted
                                ? (t('analysis.progress.completeTitle') || 'Analysis Complete!')
                                : isFailed
                                    ? (t('analysis.progress.failedTitle') || 'Analysis Failed')
                                    : (t('analysis.progress.title') || 'Analyzing Your Session')}
                        </Text>
                        {documentName && !isCompleted && !isFailed && (
                            <Text style={styles.subtitle} numberOfLines={1}>
                                {documentName}
                            </Text>
                        )}
                    </View>

                    {/* Progress Bar - only show while processing */}
                    {!isCompleted && !isFailed && (
                        <View style={styles.progressContainer}>
                            <View style={styles.progressBackground}>
                                <View
                                    style={[
                                        styles.progressFill,
                                        { width: `${effectiveState.progress}%` },
                                    ]}
                                />
                            </View>
                            <Text style={styles.progressText}>{Math.round(effectiveState.progress)}%</Text>
                        </View>
                    )}

                    {!isCompleted && !isFailed && etaText && (
                        <View style={styles.etaContainer}>
                            <View style={styles.etaRow}>
                                <MaterialIcons name="schedule" size={16} color={COLORS.primary} />
                                <Text style={styles.etaText}>{etaText}</Text>
                            </View>
                            <Text style={styles.etaCheckpointText}>
                                {t('analysis.progress.detailReason')}
                            </Text>
                            {checkpointText && (
                                <Text style={styles.etaCheckpointText}>{checkpointText}</Text>
                            )}
                            {isPossiblyStalled && (
                                <Text style={styles.etaStalledText}>
                                    {t('analysis.progress.stillWorking')}
                                </Text>
                            )}
                            {cancelError && (
                                <Text style={styles.cancelErrorText}>{cancelError}</Text>
                            )}
                        </View>
                    )}

                    {/* Stale notice: presentation only, the job keeps running */}
                    {!isCompleted && !isFailed && staleNotice && (
                        <View style={styles.staleNotice} testID="analysis-stale-notice">
                            <Text style={styles.staleNoticeText}>
                                {staleNotice === 'waitingInLine'
                                    ? t('jobs.progress.waitingInLine')
                                    : t('jobs.progress.lostTrack')}
                            </Text>
                            <View style={styles.staleNoticeActions}>
                                <TouchableOpacity
                                    style={[styles.actionButton, styles.secondaryButton]}
                                    onPress={staleNotice === 'waitingInLine' ? handleKeepWaiting : handleCheckAgain}
                                    disabled={isCheckingAgain}
                                    testID={staleNotice === 'waitingInLine' ? 'analysis-keep-waiting' : 'analysis-check-again'}
                                >
                                    <Text style={styles.secondaryButtonText}>
                                        {staleNotice === 'waitingInLine'
                                            ? t('jobs.progress.keepWaiting')
                                            : isCheckingAgain
                                                ? t('jobs.progress.checking')
                                                : t('jobs.progress.checkAgain')}
                                    </Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    )}

                    {/* Current Stage */}
                    {!isCompleted && !isFailed && !showStreamPreview && (
                        <View style={styles.stageRow}>
                            <ActivityIndicator size="small" color={COLORS.primary} style={{ marginRight: 8 }} />
                            <View style={styles.stageTextColumn}>
                                <Text style={styles.stageMessage}>{stageMessage}</Text>
                                {stageDetail && <Text style={styles.stageDetail}>{stageDetail}</Text>}
                            </View>
                        </View>
                    )}

                    {/* Streaming Preview - visible from first delta until task_complete */}
                    {showStreamPreview && (
                        <View style={styles.streamingContainer}>
                            <View style={styles.streamingHeader}>
                                <Text style={styles.streamingLabel}>
                                    {t('analysis.progress.livePreview') || 'Live Preview'}
                                </Text>
                                {isStreaming && <ActivityIndicator size="small" color={COLORS.primary} />}
                            </View>
                            <ScrollView
                                ref={scrollViewRef}
                                style={styles.streamingScroll}
                                onContentSizeChange={() =>
                                    scrollViewRef.current?.scrollToEnd({ animated: false })
                                }
                            >
                                <MarkdownRenderer content={streamingText} />
                            </ScrollView>
                        </View>
                    )}

                    {/* Stages List - hide when streaming preview is active.
                        The active stage's detail text is rendered in the top
                        stageRow above; we deliberately omit it here to avoid
                        the visible duplication users were seeing. */}
                    {!isCompleted && !isFailed && !showStreamPreview && (
                        <View style={styles.stagesList}>
                            {stages.map((stage) => (
                                <View key={stage.id} style={styles.stageItem}>
                                    <MaterialIcons
                                        name={stage.completed ? 'check-circle' : stage.active ? 'radio-button-checked' : 'radio-button-unchecked'}
                                        size={18}
                                        color={stage.completed ? COLORS.success : stage.active ? COLORS.primary : COLORS.text.muted}
                                    />
                                    <View style={styles.stageItemTextColumn}>
                                        <Text style={[
                                            styles.stageLabel,
                                            stage.completed && styles.stageLabelCompleted,
                                            stage.active && styles.stageLabelActive,
                                        ]}>
                                            {stage.label}
                                        </Text>
                                    </View>
                                </View>
                            ))}
                        </View>
                    )}

                    {/* Success State */}
                    {isCompleted && finalSessionId && (
                        <View style={styles.successContainer}>
                            <Text style={styles.successText}>
                                {t('analysis.progress.successMessage') || 'Your coaching session has been analyzed. View the detailed feedback or download as PDF.'}
                            </Text>

                            <View style={styles.actionButtons}>
                                {onViewAnalysis && (
                                    <TouchableOpacity
                                        style={[styles.actionButton, styles.primaryButton]}
                                        onPress={() => {
                                            onViewAnalysis(finalSessionId);
                                            onClose();
                                        }}
                                    >
                                        <MaterialIcons name="visibility" size={20} color="white" />
                                        <Text style={styles.primaryButtonText}>
                                            {t('analysis.progress.viewAnalysis') || 'View Analysis'}
                                        </Text>
                                    </TouchableOpacity>
                                )}

                                {onExportPdf && (
                                    <TouchableOpacity
                                        style={[styles.actionButton, styles.secondaryButton]}
                                        onPress={() => {
                                            onExportPdf(finalSessionId);
                                        }}
                                    >
                                        <MaterialIcons name="picture-as-pdf" size={20} color={COLORS.primary} />
                                        <Text style={styles.secondaryButtonText}>
                                            {t('analysis.progress.downloadPdf') || 'Download PDF'}
                                        </Text>
                                    </TouchableOpacity>
                                )}
                            </View>

                            <TouchableOpacity
                                style={styles.closeLink}
                                onPress={onClose}
                            >
                                <Text style={styles.closeLinkText}>
                                    {t('analysis.progress.backToTranscript') || 'Back to Transcript'}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    {/* Error State */}
                    {isFailed && (
                        <View style={styles.errorContainer}>
                            <Text style={styles.errorText}>
                                {effectiveState.error || t('analysis.progress.errorMessage') || 'Something went wrong. Please try again.'}
                            </Text>
                            {onRetry && (
                                <TouchableOpacity
                                    style={[styles.actionButton, styles.primaryButton]}
                                    onPress={onRetry}
                                    testID="analysis-try-again"
                                >
                                    <Text style={styles.primaryButtonText}>
                                        {t('jobs.progress.tryAgain')}
                                    </Text>
                                </TouchableOpacity>
                            )}
                            <TouchableOpacity
                                style={[styles.actionButton, onRetry ? styles.secondaryButton : styles.primaryButton]}
                                onPress={onClose}
                            >
                                <Text style={onRetry ? styles.secondaryButtonText : styles.primaryButtonText}>
                                    {t('common.close') || 'Close'}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    {/* Tip - only show while processing and not streaming */}
                    {!isCompleted && !isFailed && !showStreamPreview && (
                        <View style={styles.tipContainer}>
                            <Text style={styles.tipIcon}>💡</Text>
                            <Text style={styles.tipText}>
                                {t('analysis.progress.tip') || 'AI is reviewing your coaching session for insights, strengths, and areas for improvement.'}
                            </Text>
                        </View>
                    )}

                    {!isCompleted && !isFailed && (
                        <TouchableOpacity
                            style={[styles.cancelButton, isCancelling && styles.cancelButtonDisabled]}
                            onPress={handleCancel}
                            disabled={isCancelling}
                        >
                            <MaterialIcons name="close" size={18} color={COLORS.error} />
                            <Text style={styles.cancelButtonText}>
                                {isCancelling
                                    ? t('analysis.progress.cancelling')
                                    : t('analysis.progress.cancel')}
                            </Text>
                        </TouchableOpacity>
                    )}
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },
    container: {
        backgroundColor: COLORS.background,
        borderRadius: 20,
        padding: 28,
        width: '100%',
        maxWidth: 420,
        alignItems: 'center',
    },
    header: {
        alignItems: 'center',
        marginBottom: 24,
    },
    title: {
        fontSize: 22,
        fontWeight: '700',
        color: COLORS.text.primary,
        marginTop: 16,
        textAlign: 'center',
    },
    subtitle: {
        fontSize: 14,
        color: COLORS.text.muted,
        marginTop: 6,
        textAlign: 'center',
    },
    progressContainer: {
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    progressBackground: {
        flex: 1,
        height: 12,
        backgroundColor: COLORS.surface,
        borderRadius: 6,
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        backgroundColor: COLORS.primary,
        borderRadius: 6,
    },
    progressText: {
        fontSize: 14,
        fontWeight: '600',
        color: COLORS.text.secondary,
        marginLeft: 12,
        minWidth: 40,
        textAlign: 'right',
    },
    etaContainer: {
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(30, 64, 175, 0.06)',
        borderRadius: 10,
        paddingVertical: 8,
        paddingHorizontal: 12,
        marginTop: -4,
        marginBottom: 16,
    },
    etaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    etaText: {
        fontSize: 13,
        fontWeight: '600',
        color: COLORS.primary,
        marginLeft: 6,
        textAlign: 'center',
    },
    etaCheckpointText: {
        fontSize: 12,
        color: COLORS.text.secondary,
        marginTop: 3,
        textAlign: 'center',
    },
    etaStalledText: {
        fontSize: 12,
        color: COLORS.text.secondary,
        marginTop: 6,
        textAlign: 'center',
    },
    staleNotice: {
        backgroundColor: COLORS.surface,
        borderColor: COLORS.border,
        borderWidth: 1,
        borderRadius: 10,
        padding: 12,
        marginBottom: 16,
        gap: 10,
    },
    staleNoticeText: {
        fontSize: 14,
        color: COLORS.text.primary,
        textAlign: 'center',
    },
    staleNoticeActions: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 8,
    },
    cancelErrorText: {
        fontSize: 12,
        color: COLORS.error,
        marginTop: 6,
        textAlign: 'center',
    },
    stageRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 20,
    },
    stageMessage: {
        fontSize: 16,
        fontWeight: '600',
        color: COLORS.primary,
    },
    stageTextColumn: {
        flex: 1,
    },
    stageDetail: {
        marginTop: 3,
        fontSize: 12,
        lineHeight: 17,
        color: COLORS.text.secondary,
    },
    stagesList: {
        width: '100%',
        paddingHorizontal: 8,
        marginBottom: 20,
    },
    stageItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 6,
    },
    stageItemTextColumn: {
        flex: 1,
        marginLeft: 10,
    },
    stageLabel: {
        fontSize: 14,
        color: COLORS.text.muted,
    },
    stageItemDetail: {
        marginTop: 2,
        fontSize: 12,
        lineHeight: 16,
        color: COLORS.text.secondary,
    },
    stageLabelCompleted: {
        color: COLORS.success,
    },
    stageLabelActive: {
        color: COLORS.text.primary,
        fontWeight: '600',
    },
    streamingContainer: {
        width: '100%',
        marginTop: 4,
        marginBottom: 12,
        borderRadius: 12,
        backgroundColor: '#F9FAFB',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        overflow: 'hidden',
    },
    streamingHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingTop: 12,
        paddingBottom: 4,
    },
    streamingLabel: {
        fontSize: 12,
        fontWeight: '600',
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    streamingScroll: {
        maxHeight: 300,
        paddingHorizontal: 16,
        paddingBottom: 12,
    },
    successContainer: {
        width: '100%',
        alignItems: 'center',
    },
    successText: {
        fontSize: 15,
        color: COLORS.text.secondary,
        textAlign: 'center',
        lineHeight: 22,
        marginBottom: 24,
    },
    actionButtons: {
        width: '100%',
        gap: 12,
    },
    actionButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 14,
        paddingHorizontal: 24,
        borderRadius: 12,
        gap: 8,
    },
    primaryButton: {
        backgroundColor: COLORS.success,
    },
    primaryButtonText: {
        fontSize: 16,
        fontWeight: '700',
        color: 'white',
    },
    secondaryButton: {
        backgroundColor: 'white',
        borderWidth: 2,
        borderColor: COLORS.primary,
    },
    secondaryButtonText: {
        fontSize: 16,
        fontWeight: '600',
        color: COLORS.primary,
    },
    closeLink: {
        marginTop: 20,
        paddingVertical: 8,
    },
    closeLinkText: {
        fontSize: 14,
        color: COLORS.text.muted,
        textDecorationLine: 'underline',
    },
    errorContainer: {
        width: '100%',
        alignItems: 'center',
        backgroundColor: '#FEF2F2',
        borderRadius: 12,
        padding: 20,
    },
    errorText: {
        fontSize: 15,
        color: COLORS.error,
        textAlign: 'center',
        marginBottom: 16,
    },
    tipContainer: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        backgroundColor: 'rgba(30, 64, 175, 0.08)',
        borderRadius: 10,
        padding: 14,
        marginTop: 8,
    },
    tipIcon: {
        fontSize: 16,
        marginRight: 10,
    },
    tipText: {
        fontSize: 13,
        color: COLORS.primary,
        flex: 1,
        lineHeight: 20,
    },
    cancelButton: {
        marginTop: 14,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        minHeight: 40,
        paddingHorizontal: 14,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(239, 68, 68, 0.35)',
        backgroundColor: '#FFFFFF',
    },
    cancelButtonDisabled: {
        opacity: 0.6,
    },
    cancelButtonText: {
        fontSize: 14,
        fontWeight: '600',
        color: COLORS.error,
    },
});

export default AnalysisProgress;
